import React from 'react';

import {
    Dialog,
    DialogContent,
    DialogActions,
    AppBar,
    Toolbar,
    Typography,
    Button,
    IconButton,
    InputLabel,
    Select,
    FormControl,
    FormHelperText,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import MenuItem from "@material-ui/core/MenuItem";
import CloseIcon from '@material-ui/icons/Close';
import { LOCATIONS } from '../../../../constants';
import { getAvailableAssignedRoles } from '../../../../util/APIUtils';
import useUnleashHook from '../../../../v2/core/unleash/useUnleashHook';
import { UnleashService } from '../../../../v2/services';
import styles from './index.styles';

const useStyles = makeStyles(theme => styles(theme));

const RoleAssignmentDialog = ({ open, handleClose, userDetail, submitAssignment, useAddFacilityOnly = true }) => {
    const classes = useStyles();
    const [availableRoles, setAvailableRoles] = React.useState([]);
    const { isFlagEnabled } = useUnleashHook(UnleashService.FLAGS.V2_STAFF_ROLE_LOCATION);
    const v2StaffRoleLocationEnabled = isFlagEnabled();

    const sortedLocation = LOCATIONS
        .filter((l) => l.locationTypeId === 3 || l.locationTypeId === parseInt(process.env.REACT_APP_APDS_JURISDICTION_ID, 10))
        .sort((a, b) => {
            if (a.locationName < b.locationName) return -1;
            return a.locationName > b.locationName ? 1 : 0;
        });

    const getAvailableRoles = React.useCallback(async (location) => {
        if (location) {
            const roles = await getAvailableAssignedRoles(location);
            if (roles) {
                roles.sort((a, b) => {
                    const nameA = v2StaffRoleLocationEnabled ? a.displayName : a.roleName;
                    const nameB = v2StaffRoleLocationEnabled ? b.displayName : b.roleName;
                    if (nameA < nameB) return -1;
                    return nameA > nameB ? 1 : 0;
                });
                setAvailableRoles(roles);
            }
        } else {
            setAvailableRoles([]);
        }
    }, [v2StaffRoleLocationEnabled]);

    React.useEffect(() => {
        if (!open) setAvailableRoles([]);
    }, [open]);

    const validate = (values) => {
        const errors = {};
        if (!values.location || String(values.location).length < 1) {
            errors["location"] = "At least 1 facility is required";
        }
        if (!useAddFacilityOnly && (!values.roles || String(values.roles).length < 1)) {
            errors["roles"] = "At least 1 role is required";
        }
        return errors;
    };

    const handleSubmit = async (values, userDetails) => {
        let payload = { ...values };
        if (useAddFacilityOnly && !payload.roles && payload.location) {
            const roles = await getAvailableAssignedRoles(payload.location);
            if (roles && roles.length > 0) {
                payload.roles = roles[0].roleId;
            }
        }
        submitAssignment(payload, userDetails);
        handleClose();
    };

    return (
        <Dialog fullWidth open={open} onClose={handleClose}>
            <Formik
                initialValues={{ location: "", roles: "" }}
                validate={validate}
                onSubmit={handleSubmit}
            >
                {({ values, handleChange, handleSubmit, setFieldValue, errors, handleBlur, touched }) => (
                    <form id="main-form" onSubmit={handleSubmit}>
                        <AppBar className={classes.appBar}>
                            <Toolbar>
                                <Typography variant="h6" className={classes.title}>
                                    {useAddFacilityOnly ? 'Add Facility' : 'Assign Roles'}
                                </Typography>
                                <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                                    <CloseIcon />
                                </IconButton>
                            </Toolbar>
                        </AppBar>

                        <DialogContent className={classes.dialogContent}>
                            {useAddFacilityOnly ? (
                                <>
                                    <Typography className={classes.facilityLabel} component="label" htmlFor="add-facility-select">
                                        Facility
                                    </Typography>
                                    <FormControl variant="outlined" className={classes.field} error={errors.location && touched.location}>
                                        <InputLabel id="add-facility-label">Facility</InputLabel>
                                        <Select
                                            id="add-facility-select"
                                            inputProps={{ id: 'add-facility-select' }}
                                            labelId="add-facility-label"
                                            label="Facility"
                                            name="location"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.location || ''}
                                        >
                                            {sortedLocation.map((location) => (
                                                <MenuItem key={location.locationId} value={location.locationId}>
                                                    {location.locationName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.location && touched.location && (
                                            <FormHelperText>{errors.location}</FormHelperText>
                                        )}
                                    </FormControl>
                                </>
                            ) : (
                                <>
                                    <Typography>LOCATIONS</Typography>
                                    <FormControl variant="outlined" className={classes.field} error={errors.location && touched.location}>
                                        <InputLabel>Facility</InputLabel>
                                        <Select
                                            label="Facility"
                                            name="location"
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                                getAvailableRoles(e.target.value);
                                                handleChange(e);
                                            }}
                                            value={values.location || ''}
                                        >
                                            {sortedLocation.map((location) => (
                                                <MenuItem key={location.locationId} value={location.locationId}>
                                                    {location.locationName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.location && touched.location && (
                                            <FormHelperText>{errors.location}</FormHelperText>
                                        )}
                                    </FormControl>
                                    <Typography style={{ paddingTop: '12px' }}>ROLE</Typography>
                                    <FormControl variant="outlined" className={classes.field} error={errors.roles && touched.roles}>
                                        <InputLabel>Roles</InputLabel>
                                        <Select
                                            label="Roles"
                                            name="roles"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={!availableRoles.length}
                                            value={values.roles || ''}
                                        >
                                            {availableRoles.map((role) => (
                                                <MenuItem key={role.roleId} value={role.roleId}>
                                                    {v2StaffRoleLocationEnabled ? role.displayName : role.roleName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.roles && touched.roles && (
                                            <FormHelperText>{errors.roles}</FormHelperText>
                                        )}
                                    </FormControl>
                                </>
                            )}
                        </DialogContent>

                        <DialogActions className={classes.dialogActions}>
                            <Button onClick={handleClose} color="primary" className={classes.cancelBtn}>CANCEL</Button>
                            <Button
                                color="primary"
                                variant="contained"
                                type="submit"
                                className={classes.saveBtn}
                            >SAVE</Button>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </Dialog>
    );
};

export default RoleAssignmentDialog;
